import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Clipboard from 'clipboard';
import shallowEqual from 'shallowequal';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { CSSTransitionGroup } from 'react-transition-group';
import GiftIcon from 'react-material-icons/icons/action/card-giftcard';
import NavigateRightIcon from 'react-material-icons/icons/navigation/chevron-right';
import CopyIcon from 'react-material-icons/icons/content/content-copy';
import AppInsights from '@app/appInsights';
import {
  STATUS_IN_PROGRESS,
  TYPE_NEW_ORDER,
  TYPE_DELIVER_ORDER,
  TYPE_CANCEL_NEW_ORDER,
  TYPE_CANCEL_DELIVER_ORDER,
  QUEUE_INCOMING,
  QUEUE_BEING_DELIVERED,
  QUEUE_FINALIZED,
  ORDER_STATUS_DEALER_CANCELLED,
  ORDER_STATUS_DEALER_DECLINED,
} from '@chama/utils/constants/status';
import isCsUser from '@chama/utils/functions/is-cs-user';
import OrderStatusIcon from '@chama/ui-components/components/OrderStatusIcon';
import Tooltip from '@chama/ui-components/components/Tooltip';
import alarmIcon from '@chama/statics/assets/svg/alarm.svg';
import clockIcon from '@chama/statics/assets/svg/clock.svg';
import phoneIcon from '@chama/statics/assets/svg/Icon_Flat_Others_Phone.svg';
import { isBlocked } from '@chama/utils/functions/is-dealer-blocked-or-suspended';
import BlockIcon from 'react-material-icons/icons/content/block';
import OrderClockTimer from '../OrderClockTimer';
import './index.css';

const propTypes = {
  order: PropTypes.object.isRequired,
  active: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
  dismissOrder: PropTypes.func.isRequired,
  cancelDelivery: PropTypes.func.isRequired,
  setCurrentOrder: PropTypes.func.isRequired,
  isAssignedToCurrentUser: PropTypes.bool.isRequired,
  offset: PropTypes.number.isRequired,
  dealer: PropTypes.object,
  userId: PropTypes.string.isRequired,
};

const defaultProps = {
  dealer: undefined,
};

class OrderSnippet extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isOptionsOpen: false,
    };

    this.clipboardElementId = 'copy-order-id-button';
  }

  track = eventName => {
    const {
      order: {
        id: orderId,
        status: orderStatus,
        currentTicket: { id: ticketId },
      },
      userId,
    } = this.props;
    const data = { orderId, orderStatus, ticketId, userId };
    AppInsights.trackEvent(eventName, data);
  };

  componentDidMount() {
    this.track('mdt-order-update-rendered');
  }

  componentDidUpdate(prevProps) {
    const {
      order: { status: orderStatus },
    } = this.props;
    const {
      order: { status: prevOrderStatus },
    } = prevProps;

    if (prevOrderStatus !== orderStatus) this.track('mdt-order-update-rendered');
  }

  shouldComponentUpdate(nextProps, nextState) {
    const nextEtag = nextProps.order.currentTicket.eTag;
    const eTagChanged = nextEtag !== this.props.order.currentTicket.eTag;
    const offsetChanged = nextProps.offset !== this.props.offset;
    const activeChanged = nextProps.active !== this.props.active;
    const hasStateChanged = !shallowEqual(this.state, nextState);
    const hasDealerChanged = () => {
      if (nextProps.dealer && !this.props.dealer) {
        return true;
      }

      if (nextProps.dealer && this.props.dealer) {
        if (nextProps.dealer.name !== this.props.dealer.name) {
          return true;
        }

        if (nextProps.dealer.isActive !== this.props.dealer.isActive) {
          return true;
        }

        if (nextProps.dealer.id !== this.props.dealer.id) {
          return true;
        }
      }

      return false;
    };

    return hasStateChanged || eTagChanged || offsetChanged || activeChanged || hasDealerChanged();
  }

  handleRef = refName => {
    return ref => {
      this[refName] = ref;
    };
  };

  onOrderMouseEnter = () => {
    this.clipboard = new Clipboard(`#${this.clipboardElementId}`);

    this.setState({ isOptionsOpen: true });
  };

  onOrderMouseLeave = () => {
    if (this.clipboard) {
      this.clipboard.destroy();
    }

    this.setState({ isOptionsOpen: false });
  };

  renderOrderOptions = () => {
    const { isOptionsOpen } = this.state;
    const {
      order: { id },
      intl: { formatMessage },
    } = this.props;

    if (isOptionsOpen) {
      const copyBtnTooltip = formatMessage({
        id: 'order_snippet.copy_id',
        defaultMessage: 'Copy order ID',
      });

      return (
        <div key="OrderSnippet-options" className="OrderSnippet__options">
          <Tooltip position="left" title={copyBtnTooltip}>
            <button
              className="OrderSnippet__options_btn"
              id={this.clipboardElementId}
              data-clipboard-text={id}
              type="button"
            >
              <CopyIcon className="OrderSnippet__options_btn_icon" />
            </button>
          </Tooltip>
        </div>
      );
    }

    return null;
  };

  goToOrder = () => {
    const {
      setCurrentOrder,
      order: { id },
    } = this.props;

    this.track('mdt-order-tile-pressed');
    setCurrentOrder(id);
  };

  dismissOrder = () => {
    const {
      dismissOrder,
      order: {
        currentTicket: { id, eTag },
      },
    } = this.props;

    return dismissOrder(id, eTag);
  };

  cancelDelivery = () => {
    const {
      cancelDelivery,
      order: {
        currentTicket: { id, eTag },
      },
    } = this.props;

    return cancelDelivery(id, eTag);
  };

  onCancelNewOrderConfirm = () => {
    const { setCurrentOrder } = this.props;

    this.dismissOrder().then(() => {
      setCurrentOrder(null);
    });
  };

  onCancelDeliverOrderConfirm = () => {
    const { setCurrentOrder } = this.props;

    this.cancelDelivery().then(() => {
      setCurrentOrder(null);
    });
  };

  isConsumerCanceled = () => {
    const {
      order: {
        currentTicket: { type },
      },
    } = this.props;

    return type === TYPE_CANCEL_NEW_ORDER || type === TYPE_CANCEL_DELIVER_ORDER;
  };

  isDealerCanceled = () => {
    const {
      order: { status },
    } = this.props;

    return status === ORDER_STATUS_DEALER_CANCELLED || status === ORDER_STATUS_DEALER_DECLINED;
  };

  getComponentClass = () => {
    const {
      order: {
        voucher,
        currentTicket: { queue },
      },
      active,
    } = this.props;

    return classNames('OrderSnippet', {
      'is-active': active,
      'is-incoming': queue === QUEUE_INCOMING,
      'is-being_delivered': queue === QUEUE_BEING_DELIVERED,
      'is-finalized': queue === QUEUE_FINALIZED,
      'is-canceled': this.isConsumerCanceled() || this.isDealerCanceled(),
      'has-voucher': !!voucher,
    });
  };

  renderAssignee = () => {
    const {
      isAssignedToCurrentUser,
      order: {
        currentTicket: { status, assignedToUserEmail },
      },
    } = this.props;

    if (assignedToUserEmail) {
      const isInProgress = status === STATUS_IN_PROGRESS;
      const assigneeClass = classNames('OrderSnippet__assignee', {
        'OrderSnippet__assignee--open_by_other_user': !isAssignedToCurrentUser && isInProgress,
      });
      const assigneeInitials = assignedToUserEmail.substr(0, 2);
      const assigneeTooltip = <strong>{assignedToUserEmail}</strong>;

      return (
        <Tooltip position="bottom" html={assigneeTooltip}>
          <div key="assignee" className={assigneeClass}>
            {assigneeInitials}
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  renderMainInfo = () => {
    const {
      order: {
        address,
        currentTicket: { type },
      },
      intl: { formatMessage },
    } = this.props;

    if (type === TYPE_CANCEL_NEW_ORDER || type === TYPE_CANCEL_DELIVER_ORDER) {
      return formatMessage({
        id: 'cancelled_by_customer',
        defaultMessage: 'Cancelled by customer',
      });
    }

    return address;
  };

  renderVoucherIndicator = () => {
    const {
      order: { voucher },
    } = this.props;

    if (voucher) {
      return (
        <div className="OrderSnippet__voucher_display">
          <GiftIcon className="OrderSnippet__voucher_display_icon" />
        </div>
      );
    }

    return null;
  };

  renderTelephoneOrderIndicator = () => {
    const {
      order: { salesChannel },
      intl: { formatMessage },
    } = this.props;

    if (!salesChannel || salesChannel !== 'shop') {
      return null;
    }

    const tooltipText = formatMessage({
      id: 'order_snippet.telephone_order',
    });

    return (
      <Tooltip
        className="OrderSnippet__telephone-order-tooltip"
        position="left"
        title={tooltipText}
      >
        <div className="OrderSnippet__telephone-order">
          <img width={20} height={20} src={phoneIcon} alt="telephone" />
        </div>
      </Tooltip>
    );
  };

  renderOrderTimeIndicator = () => {
    const {
      offset,
      order: {
        eta,
        creationTime,
        currentTicket: { queue, type, modificationTime },
      },
    } = this.props;
    const canShowClockTimer =
      (queue === QUEUE_INCOMING && type === TYPE_NEW_ORDER) ||
      (queue === QUEUE_BEING_DELIVERED && type === TYPE_DELIVER_ORDER);

    if (canShowClockTimer) {
      return (
        <OrderClockTimer
          eta={eta}
          creationTime={creationTime}
          offset={offset}
          ticketQueue={queue}
          iconImgSrc={clockIcon}
          attentionImgSrc={alarmIcon}
          isConsumerCanceled={this.isConsumerCanceled()}
          isCountdown={queue === QUEUE_BEING_DELIVERED}
        />
      );
    }

    const dateValue = queue === QUEUE_FINALIZED ? modificationTime : creationTime;
    return (
      <div className="OrderSnippet__duration OrderSnippet__duration--date">
        <FormattedDate value={dateValue} month="numeric" day="numeric" />
      </div>
    );
  };

  render() {
    const { dealer, active } = this.props;
    const {
      order: {
        id,
        status,
        currentTicket: { assignedToUserId },
        consumerDelivered,
      },
    } = this.props;
    const splittedId = id.split('|');
    const shortId = splittedId[splittedId.length - 1];
    const shopNameClass = classNames('OrderSnippet__shop_name', {
      'OrderSnippet__shop_name--active': dealer && dealer.isActive,
      'OrderSnippet__shop_name--unactive': dealer && !dealer.isActive,
    });

    return (
      <div id={`order-${shortId}`} className={this.getComponentClass()}>
        <div onMouseEnter={this.onOrderMouseEnter} onMouseLeave={this.onOrderMouseLeave}>
          <div onClick={this.goToOrder} className="OrderSnippet__content">
            {this.renderOrderTimeIndicator()}

            <div className="OrderSnippet__main_info">
              {dealer && (
                <div className={shopNameClass}>
                  <span>{dealer.name}</span>
                  {isBlocked(dealer) && (
                    <span className="OrderSnippet__dealer_not_live">
                      <BlockIcon className="OrderSnippet__dealer_not_live_icon" color="red" />
                      <FormattedMessage
                        id={isBlocked(dealer) ? 'dealer_blocked' : 'dealer_suspended'}
                        defaultMessage={isBlocked(dealer) ? 'Blocked' : 'Suspended'}
                      />
                    </span>
                  )}
                  <span className="OrderSnippet__dealer_id"> ({dealer.id})</span>
                </div>
              )}
              <div className="OrderSnippet__address">{this.renderMainInfo()}</div>
              <div className="OrderSnippet__order_id">{id}</div>
            </div>

            <div className="OrderSnippet__status_info">
              <CSSTransitionGroup
                transitionName="OrderSnippet-appear-transition"
                transitionEnterTimeout={300}
                transitionLeaveTimeout={300}
              >
                {this.renderAssignee()}
              </CSSTransitionGroup>

              <OrderStatusIcon
                orderStatus={status}
                isCsUser={isCsUser(assignedToUserId)}
                consumerDelivered={consumerDelivered}
              />

              {this.renderVoucherIndicator()}

              {this.renderTelephoneOrderIndicator()}
            </div>

            <CSSTransitionGroup
              component="div"
              className="OrderSnippet__order_open"
              transitionName="OrderSnippet-appear-transition"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {active && (
                <NavigateRightIcon key="active-icon" className="OrderSnippet__order_open_icon" />
              )}
            </CSSTransitionGroup>
          </div>

          <CSSTransitionGroup
            transitionName="OrderSnippet-options-transition"
            transitionEnterTimeout={300}
            transitionLeaveTimeout={200}
          >
            {this.renderOrderOptions()}
          </CSSTransitionGroup>
        </div>
      </div>
    );
  }
}

OrderSnippet.propTypes = propTypes;
OrderSnippet.defaultProps = defaultProps;

export default OrderSnippet;



// WEBPACK FOOTER //
// ./src/modules/orders/components/OrderSnippet/index.jsx